import { useState } from "react";
import type { ExtendChannel } from "../../../types/chatExtension";
import { formatMoney } from "../../../utils/format";
import {
  usePaymentChannels,
  logoFor,
  needsPhone,
  needsEmail,
  operatorFor,
  prefixFor,
} from "../../../hooks/usePaymentChannels";

interface Props {
  amount: number;
  currency: string;
  onSubmit: (payload: {
    channel: ExtendChannel;
    msisdn?: string;
    mobileMoneyOperator?: "tnm" | "airtel";
    email?: string;
  }) => Promise<void>;
  submitting: boolean;
  error: string;
}

export function PaymentPicker({
  amount,
  currency,
  onSubmit,
  submitting,
  error,
}: Props) {
  const { channels, loading, error: loadError } = usePaymentChannels();
  const [selected, setSelected] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const config = channels.find((c) => c.slug === selected) || null;
  const prefix = config ? prefixFor(config) : "";

  function handlePhoneChange(raw: string) {
    const digits = raw.replace(/\D/g, "");
    const rest = digits.startsWith(prefix)
      ? digits.slice(prefix.length)
      : digits.startsWith("265")
        ? digits.slice(3)
        : digits;
    setPhone(prefix + rest.slice(0, 8));
  }

  const canSubmit =
    !!config &&
    !submitting &&
    (!needsPhone(config) || phone.length === prefix.length + 8) &&
    (!needsEmail(config) || email.trim().length > 3);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!config) return;
    await onSubmit({
      channel: config.slug as ExtendChannel,
      msisdn: needsPhone(config) ? phone : undefined,
      mobileMoneyOperator: operatorFor(config),
      email: needsEmail(config) ? email.trim() : undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-2">
          Pay with
        </p>

        {loading && (
          <p className="text-[13px] text-muted">Loading payment methods…</p>
        )}
        {!loading && loadError && (
          <p className="text-[13px] text-rose-dark">{loadError}</p>
        )}
        {!loading && !loadError && channels.length === 0 && (
          <p className="text-[13px] text-rose-dark">
            No payment methods are currently available.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {channels.map((ch) => (
            <button
              key={ch.slug}
              type="button"
              onClick={() => {
                setSelected(ch.slug);
                setPhone(needsPhone(ch) ? prefixFor(ch) : "");
              }}
              className={`flex items-center gap-3 text-left p-4 rounded-2xl border transition-all duration-200 ${
                selected === ch.slug
                  ? "border-rose bg-blush"
                  : "border-black/10 bg-white hover:border-black/20"
              }`}
            >
              {logoFor(ch) && (
                <img
                  src={logoFor(ch)}
                  alt={ch.name}
                  className="w-6 h-6 object-contain flex-shrink-0"
                />
              )}
              <p className="text-[14px] font-semibold text-black">{ch.name}</p>
            </button>
          ))}
        </div>
      </div>

      {config && needsPhone(config) && (
        <div>
          <label className="block text-[13px] font-semibold text-black mb-2">
            Mobile number
          </label>
          <input
            type="tel"
            required
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={`e.g. ${prefix}12345678`}
            className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
          />
        </div>
      )}

      {config && needsEmail(config) && (
        <div>
          <label className="block text-[13px] font-semibold text-black mb-2">
            Email for receipt
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors"
          />
        </div>
      )}

      {error && <p className="text-[13px] text-rose-dark">{error}</p>}

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full bg-magenta text-white text-sm font-semibold py-3.5 rounded-full hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {submitting
          ? "Starting checkout…"
          : `Pay ${formatMoney(amount, currency)}`}
      </button>
    </form>
  );
}
